import { getAlertThunk } from '@redux/features/alert.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DebouncedInput from 'Shared/DebouncedInput';

const Alert: FC = () => {
    // -----------------------------------------------------------------
    
    const [page, setPage] = useState<number>(1);
    const [productId, setProductId] = useState<string>('');
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { alerts, loading } = useAppSelector((state: RootState) => state.alertState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Handle search function (debounced)
    const handleProductIdSearch = (value: string) => {
        setProductId(value);
        setPage(1); // Reset to first page when searching
    };

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to format alert type
    const formatAlertType = (alertType: string | null | undefined) => {
        if (!alertType) return '-';
        return alertType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Helper function to get severity badge color
    const getSeverityColor = (severity: string | null | undefined) => {
        if (!severity) return 'bg-gray-100 text-gray-800';
        switch (severity.toUpperCase()) {
            case 'CRITICAL':
                return 'bg-red-100 text-red-800';
            case 'WARNING':
                return 'bg-yellow-100 text-yellow-800';
            case 'INFO':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Helper function to get read status badge
    const getReadStatusBadge = (isRead: boolean | null | undefined) => {
        return isRead ? (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Read</span>
        ) : (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-800">Unread</span>
        );
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        dispatch(
            getAlertThunk({
                size: 10,
                page: page - 1,
                productId: productId || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, productId]);
    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle={`Alerts`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Alerts List</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Product ID</label>
                                <DebouncedInput
                                    placeholder="Search by Product ID"
                                    onChange={(value) => {
                                        // Only update local state, don't trigger search immediately
                                        // The searchFn will handle the actual search after debounce
                                    }}
                                    searchFn={handleProductIdSearch}
                                    searchValue={productId}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Alert Type</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Product Batch ID
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Current Quantity
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Threshold Quantity
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Expiry Date</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Days Until Expiry
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Severity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Notified At</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Created At</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={alerts?.content?.length ?? 0} animationKey={`alert-${page}`}>
                                            {(alerts as any)?.content && (alerts as any).content.length > 0 ? (
                                                (alerts as any).content.map((alert: any) => (
                                                    <AnimatedTableRow
                                                        rowKey={alert?.id}
                                                        detailRecord={alert}
                                                        className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                    >
                                                        <td className="px-3.5 py-2.5">{alert?.id || `-`}</td>
                                                        <td className="px-3.5 py-2.5">
                                                            <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                                                                {formatAlertType(alert?.alertType)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3.5 py-2.5">{alert?.productName || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{alert?.productId || `-`}</td>
                                                        <td className="px-3.5 py-2.5">
                                                            {alert?.productBatchId || `-`}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {alert?.currentQuantity ?? `-`}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {alert?.thresholdQuantity !== undefined &&
                                                            alert?.thresholdQuantity !== null
                                                                ? alert.thresholdQuantity
                                                                : `-`}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {alert?.expiryDate
                                                                ? new Date(alert.expiryDate).toLocaleDateString(
                                                                      'en-US',
                                                                      {
                                                                          year: 'numeric',
                                                                          month: 'short',
                                                                          day: 'numeric',
                                                                      }
                                                                  )
                                                                : `-`}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {alert?.daysUntilExpiry !== undefined &&
                                                            alert?.daysUntilExpiry !== null
                                                                ? `${alert.daysUntilExpiry} day${alert.daysUntilExpiry !== 1 ? 's' : ''}`
                                                                : `-`}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            <span
                                                                className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(alert?.severity)}`}
                                                            >
                                                                {alert?.severity || `-`}
                                                            </span>
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {getReadStatusBadge(alert?.isRead)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatDate(alert?.notifiedAt)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatDate(alert?.createdAt)}
                                                        </td>
                                                    </AnimatedTableRow>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={13} className="px-3.5 py-8 text-center text-gray-500">
                                                        No alerts found
                                                    </td>
                                                </tr>
                                            )}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(alerts as any)?.pageable?.pageSize || 10}
                                    total={(alerts as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={(alerts as any)?.numberOfElements || (alerts as any)?.content?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Alert;
